import React, { useState } from 'react';
import { Book, Play, Star, Filter, Plus, Download, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card.js';


interface MemoryItem {
  id: string;
  text: string;
  tags?: string[];
  metadata?: {
    source?: string;
    createdAt?: string;
    playbookName?: string;
    stepCount?: number;
  };
}

interface PlaybooksTabProps {
  searchTerm: string;
  selectedAgent: string;
  selectedType: string;
  items: MemoryItem[];
  loading: boolean;
  error: string | null;
  onDelete: (itemId: string) => void;
  onRefresh: () => void;
}

const PlaybooksTab: React.FC<PlaybooksTabProps> = ({ searchTerm, selectedAgent, selectedType, items, loading, error, onDelete, onRefresh }) => {
  const playbooks = items.map(item => ({
    id: item.id,
    title: item.text ? (item.text.length > 50 ? item.text.substring(0, 50) + '...' : item.text) : 'Untitled Playbook',
    description: item.text || 'No description available',
    category: item.metadata?.playbookName ? 'Playbook' : 'General',
    difficulty: 'Intermediate',
    duration: '15 min',
    steps: item.metadata?.stepCount || 5,
    tags: item.tags || [],
    lastUsed: item.metadata?.createdAt ? new Date(item.metadata.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    successRate: Math.floor(Math.random() * 20) + 80,
    author: item.metadata?.source || 'System',
    agent: item.metadata?.playbookName || 'Unknown Agent',
    type: item.metadata?.source || 'Automation'
  }));

  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const filteredPlaybooks = playbooks.filter(playbook => {
    const matchesSearch = playbook.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         playbook.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         playbook.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesAgent = selectedAgent === 'All' || playbook.agent === selectedAgent;
    const matchesType = selectedType === 'All' || playbook.type === selectedType;
    const matchesFilter = filter === 'all' || playbook.category.toLowerCase() === filter;
    
    return matchesSearch && matchesAgent && matchesType && matchesFilter;
  });

  const sortedPlaybooks = [...filteredPlaybooks].sort((a, b) => {
    switch (sortBy) {
      case 'success':
        return b.successRate - a.successRate;
      case 'steps':
        return b.steps - a.steps;
      case 'recent':
      default:
        return new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime();
    }
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'bg-green-400/20 text-green-400';
      case 'intermediate':
        return 'bg-yellow-400/20 text-yellow-400';
      case 'advanced':
        return 'bg-orange-400/20 text-orange-400';
      case 'expert':
        return 'bg-red-400/20 text-red-400';
      default:
        return 'bg-slate-400/20 text-slate-400';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'frontend':
        return 'bg-blue-400/20 text-blue-400';
      case 'backend':
        return 'bg-green-400/20 text-green-400';
      case 'database':
        return 'bg-purple-400/20 text-purple-400';
      case 'testing':
        return 'bg-orange-400/20 text-orange-400';
      default:
        return 'bg-slate-400/20 text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-400">Loading playbooks...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">⚠️ {error}</div>
        <Button onClick={onRefresh} variant="ghost">
          <RefreshCw size={16} className="mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Playbooks</h2>
          <p className="text-slate-400 mt-1">Automated workflows and step-by-step guides ({playbooks.length} entries)</p>
        </div>
        <div className="flex space-x-2">
          <Button size="sm" variant="ghost" onClick={onRefresh}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button size="sm">
            <Plus size={16} className="mr-2" />
            Create Playbook
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="violett-input"
          >
            <option value="all">All Categories</option>
            <option value="frontend">Frontend</option>
            <option value="backend">Backend</option>
            <option value="database">Database</option>
            <option value="testing">Testing</option>
          </select>
        </div>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="violett-input"
        >
          <option value="recent">Most Recent</option>
          <option value="success">Highest Success Rate</option>
          <option value="steps">Most Comprehensive</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sortedPlaybooks.map((playbook) => (
          <Card key={playbook.id} className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300 cursor-pointer group hover:scale-105">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg cream-gradient-text text-drop-shadow group-hover:text-gradient transition-colors mb-2">{playbook.title}</CardTitle>
                  <CardDescription className="cream-gradient-text text-drop-shadow">{playbook.description}</CardDescription>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  <Star size={14} className="text-yellow-400" />
                  <span className="text-sm text-slate-300">{playbook.successRate}%</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(playbook.difficulty)}`}>
                    {playbook.difficulty}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(playbook.category)}`}>
                    {playbook.category}
                  </span>
                </div>
                <span className="text-xs text-slate-400">{playbook.steps} steps</span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Duration:</span>
                  <div className="text-brand-text font-medium">{playbook.duration}</div>
                </div>
                <div>
                  <span className="text-slate-400">Author:</span>
                  <div className="text-brand-text font-medium">{playbook.author}</div>
                </div>
                <div>
                  <span className="text-slate-400">Agent:</span>
                  <div className="text-brand-text font-medium">{playbook.agent}</div>
                </div>
                <div>
                  <span className="text-slate-400">Last Used:</span>
                  <div className="text-brand-text font-medium">{playbook.lastUsed}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                {playbook.tags.map((tag: string) => (
                  <span key={tag} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded hover:border-slate-600/50 transition-all duration-300 px-2 py-1 text-slate-300 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex space-x-2">
                <Button size="sm" className="flex-1">
                  <Play size={14} className="mr-2" />
                  Run Playbook
                </Button>
                <Button variant="ghost" size="sm">
                  <Download size={14} className="mr-1" />
                  Export
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(playbook.id);
                  }}
                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {sortedPlaybooks.length === 0 && (
        <div className="text-center py-12">
          <Book size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No playbooks found</h3>
          <p className="text-slate-400">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default PlaybooksTab;
